// Rich Assistant output (NEXUS-763): markdown with copyable code, mermaid diagrams, sanitized SVG and sandboxed HTML.
// Nothing here runs model-supplied script: SVG goes through sanitizeSvg (allow-list rebuild), HTML only ever lives in an
// <iframe sandbox="" srcdoc> with a no-network CSP, and mermaid output is sanitized like any other SVG.
import { useEffect, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { renderMarkdown } from '../lib/markdown';
import { blockFileName, htmlDocument, sanitizeSvg, splitRich } from '../lib/rich';
import type { RichKind } from '../lib/rich';
import { shareFiles, svgShareFiles, textFile } from '../lib/share';

export function downloadText(name: string, mime: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: `${mime};charset=utf-8` }));
  const a = document.createElement('a');
  a.href = url; a.download = name; a.rel = 'noopener';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Markdown HTML with working Copy buttons on code blocks. */
export function Prose({ markdown }: { markdown: string }) {
  const html = useMemo(() => renderMarkdown(markdown), [markdown]);
  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    const btn = (e.target as HTMLElement).closest('button[data-copy]') as HTMLButtonElement | null;
    if (!btn) return;
    const code = btn.closest('.code-block')?.querySelector('code')?.textContent ?? '';
    void navigator.clipboard?.writeText(code.replace(/\n$/, '')).then(() => {
      btn.textContent = 'Copied'; setTimeout(() => { btn.textContent = 'Copy'; }, 1500);
    }, () => { btn.textContent = 'Copy failed'; });
  };
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
  return <div className="prose" onClick={onClick} dangerouslySetInnerHTML={{ __html: html }} />;
}

// mermaid is large: load it only when a diagram is on screen, and render one at a time (it is not re-entrant).
let queue: Promise<unknown> = Promise.resolve();
let seq = 0;
async function renderMermaid(code: string): Promise<string> {
  const job = queue.then(async () => {
    const { default: mermaid } = await import('mermaid');
    mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: 'dark', htmlLabels: false, flowchart: { htmlLabels: false }, fontFamily: 'sans-serif' });
    const { svg } = await mermaid.render(`mmd-${Date.now()}-${seq++}`, code);
    const clean = sanitizeSvg(svg);
    if (!clean) throw new Error('the diagram produced no drawable output');
    return clean;
  });
  queue = job.catch(() => undefined);
  return job;
}

function useRendered(kind: RichKind, code: string): { svg: string | null; error: string | null } {
  const [state, setState] = useState<{ svg: string | null; error: string | null }>({ svg: null, error: null });
  useEffect(() => {
    let live = true;
    if (kind === 'svg') {
      const svg = sanitizeSvg(code);
      setState(svg ? { svg, error: null } : { svg: null, error: 'not a drawable SVG' });
    } else if (kind === 'mermaid') {
      setState({ svg: null, error: null });
      renderMermaid(code).then((svg) => { if (live) setState({ svg, error: null }); },
        (e) => { if (live) setState({ svg: null, error: e instanceof Error ? e.message.split('\n')[0] : String(e) }); });
    }
    return () => { live = false; };
  }, [kind, code]);
  return state;
}

function Body({ kind, code, full }: { kind: RichKind; code: string; full?: boolean }) {
  const { svg, error } = useRendered(kind, code);
  const doc = useMemo(() => (kind === 'html' ? htmlDocument(code) : ''), [kind, code]);
  if (kind === 'html') {
    return <iframe className={`rich-frame${full ? ' full' : ''}`} sandbox="" referrerPolicy="no-referrer" srcDoc={doc} title="HTML preview" />;
  }
  if (error) return <div className="rich-error"><div className="muted small">Could not draw this {kind}: {error}</div><pre>{code}</pre></div>;
  if (!svg) return <div className="muted small"><span className="spinner" /> Drawing…</div>;
  return <div className={`rich-svg${full ? ' full' : ''}`} dangerouslySetInnerHTML={{ __html: svg }} />;
}

export function RichBlock({ kind, code, n = 1 }: { kind: RichKind; code: string; n?: number }) {
  const [big, setBig] = useState(false);
  const { svg } = useRendered(kind, code);
  const save = () => {
    if (kind === 'html') downloadText(blockFileName(kind, n), 'text/html', code);
    else if (svg) downloadText(blockFileName(kind, n), 'image/svg+xml', svg);
  };
  const [note, setNote] = useState<string | null>(null);
  const share = async () => {
    try {
      const name = blockFileName(kind, n);
      const files = kind === 'html' ? [textFile(name, 'text/html', code)] : svg ? await svgShareFiles(name, svg) : [];
      if (!files.length) return;
      const r = await shareFiles(files, name);
      setNote(r === 'downloaded' ? 'Downloaded (sharing is not available in this browser)' : null);
    } catch (e) { setNote(e instanceof Error ? e.message : String(e)); }
  };
  useEffect(() => {
    if (!big) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setBig(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [big]);
  return (
    <figure className="rich-block">
      <figcaption className="rich-head">
        <span>{kind === 'mermaid' ? 'Diagram' : kind === 'svg' ? 'Graphic' : 'HTML preview'}</span>
        <span className="spacer" />
        <button type="button" className="btn small" onClick={() => setBig(true)}>Open full size</button>
        <button type="button" className="btn small" onClick={save} disabled={kind !== 'html' && !svg}>Save</button>
        <button type="button" className="btn small" onClick={() => void share()} disabled={kind !== 'html' && !svg}>Share</button>
      </figcaption>
      {note ? <div className="muted small">{note}</div> : null}
      <Body kind={kind} code={code} />
      {big ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Full size" onClick={() => setBig(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="rich-head">
              <span>{kind === 'mermaid' ? 'Diagram' : kind === 'svg' ? 'Graphic' : 'HTML preview'}</span>
              <span className="spacer" />
              <button type="button" className="btn small" onClick={save} disabled={kind !== 'html' && !svg}>Save</button>
        <button type="button" className="btn small" onClick={() => void share()} disabled={kind !== 'html' && !svg}>Share</button>
              <button type="button" className="btn small" onClick={() => setBig(false)}>Close</button>
            </div>
            <Body kind={kind} code={code} full />
          </div>
        </div>
      ) : null}
    </figure>
  );
}

/** A full assistant reply: markdown segments and rich blocks in order. */
export function RichMessage({ content }: { content: string }) {
  const segments = useMemo(() => splitRich(content), [content]);
  let n = 0;
  return (
    <div className="rich-message">
      {segments.map((s, i) => (s.type === 'md'
        ? <Prose key={i} markdown={s.text} />
        : <RichBlock key={i} kind={s.kind} code={s.code} n={++n} />))}
    </div>
  );
}
